import { downloadCSV } from 'react-admin';
import { EVENT_TYPES, type Event } from '../dataProvider';

/**
 * Admin-side helpers for the events explorer (D-events-explorer, Phase 9), the read-only
 * debug / search tool over the events stream. The eventType facet uses the LOCKED 25-value
 * catalog as fixed choices (a fixed contract, not derived from reality), and the CSV export
 * is the FIRST export in the panel (every other list sets exporter false).
 */

/** The eventType facet choices, the locked catalog as fixed SelectInput choices. */
export const EVENT_TYPE_CHOICES = EVENT_TYPES.map((t) => ({ id: t, name: t }));

/** The CSV columns, in order. The heterogeneous properties bag is one JSON column. */
export const EVENT_CSV_COLUMNS = [
  'id',
  'userId',
  'eventType',
  'occurredAt',
  'market',
  'properties',
] as const;

const csvCell = (value: string): string => `"${value.replace(/"/g, '""')}"`;

/**
 * The events as a CSV string (a pure function, so the column shaping is testable). The
 * per-type properties bag is serialized to a SINGLE json column: the event set is
 * heterogeneous, so a flattened wide table would be ragged. One properties column carrying
 * the JSON string is clean and lossless.
 */
export const eventsToCsv = (events: readonly Event[]): string => {
  const header = EVENT_CSV_COLUMNS.join(',');
  const rows = events.map((e) =>
    [
      e.id,
      e.userId,
      e.eventType,
      e.occurredAt,
      e.market,
      JSON.stringify(e.properties ?? {}),
    ]
      .map((c) => csvCell(String(c)))
      .join(','),
  );
  return [header, ...rows].join('\n');
};

/**
 * The events list exporter. react-admin passes the CURRENTLY FILTERED set, so the export
 * respects the active facets. Enabled on the List (instead of exporter false), the first
 * export in the panel.
 */
export const eventsExporter = (events: Event[]): void => {
  downloadCSV(eventsToCsv(events), 'events');
};
